/**
 * onboardingSteps.ts — Joyride step factories for data + preview phases.
 */
import type { Step } from "react-joyride";
import type { TourPhase } from "@/utils/onboardingStore";

type TFn = (key: string, params?: Record<string, string | number>) => string;

/** Only keep steps whose targets currently exist in the DOM. */
export function filterExistingSteps(steps: Step[]): Step[] {
  if (typeof document === "undefined") return steps;
  return steps.filter((s) => {
    const target = s.target;
    if (typeof target !== "string") return true;
    if (target === "body") return true;
    try {
      return Boolean(document.querySelector(target));
    } catch {
      return false;
    }
  });
}

export function buildTourSteps(phase: TourPhase, t: TFn): Step[] {
  if (phase === "data") {
    return [
      {
        target: "body",
        placement: "center",
        skipBeacon: true,
        title: t("tour.dataWelcomeTitle"),
        content: t("tour.dataWelcomeBody"),
      },
      {
        target: '[data-tour="tour-excel-upload"]',
        placement: "bottom",
        skipBeacon: true,
        title: t("tour.dataExcelTitle"),
        content: t("tour.dataExcelBody"),
      },
      {
        target: '[data-tour="tour-group-column"]',
        placement: "left",
        skipBeacon: true,
        title: t("tour.dataGroupTitle"),
        content: t("tour.dataGroupBody"),
      },
      {
        target: '[data-tour="tour-continue-preview"]',
        placement: "top",
        skipBeacon: true,
        title: t("tour.dataContinueTitle"),
        content: t("tour.dataContinueBody"),
      },
      {
        target: '[data-tour="tour-help"]',
        placement: "bottom",
        skipBeacon: true,
        title: t("tour.dataHelpTitle"),
        content: t("tour.dataHelpBody"),
      },
    ];
  }

  return [
    {
      target: "body",
      placement: "center",
      skipBeacon: true,
      title: t("tour.previewWelcomeTitle"),
      content: t("tour.previewWelcomeBody"),
    },
    {
      target: '[data-tour="tour-template-chip"]',
      placement: "bottom",
      skipBeacon: true,
      title: t("tour.previewTemplateTitle"),
      content: t("tour.previewTemplateBody"),
    },
    {
      target: '[data-tour="tour-paper-chip"]',
      placement: "bottom",
      skipBeacon: true,
      title: t("tour.previewPaperTitle"),
      content: t("tour.previewPaperBody"),
    },
    {
      target: '[data-tour="tour-print-list"]',
      placement: "right",
      skipBeacon: true,
      title: t("tour.previewListTitle"),
      content: t("tour.previewListBody"),
    },
    {
      target: '[data-tour="tour-export-actions"]',
      placement: "top",
      skipBeacon: true,
      title: t("tour.previewExportTitle"),
      content: t("tour.previewExportBody"),
    },
  ];
}
